// frontend/src/pages/Dashboard.tsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Clock, MapPin, Users, Bell, Plus, Sparkles, Trophy, Star, HeartHandshake, Copy, Share } from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import CreateEventModal from '../components/CreateEventModal';
import EventDetailModal from '../components/EventDetailModal';
import EventParticipantsModal from '../components/EventParticipantsModal';
import CreateServiceModal from '../components/CreateServiceModal';
import CreateProductModal from '../components/CreateProductModal';
// GlobalSearch removed from Dashboard; using inline DashboardSearch instead
import DashboardSearch from '../components/DashboardSearch';
// Removed booking-related pages
import AllEvents from './AllEvents';
// Notifications page removed from Dashboard navigation per request
import SportEvents from './SportEvents';

dayjs.extend(relativeTime);

import { API_URL } from '../config/api';
const API = API_URL.replace(/\/api$/, '');

// Comprehensive Sports Categories
const ALL_SPORTS = [
  // Olympic Sports
  { name: 'Football/Soccer', category: 'Team Sports', icon: '⚽', popular: true },
  { name: 'Basketball', category: 'Team Sports', icon: '🏀', popular: true },
  { name: 'Volleyball', category: 'Team Sports', icon: '🏐', popular: true },
  { name: 'Tennis', category: 'Racquet Sports', icon: '🎾', popular: true },
  { name: 'Swimming', category: 'Aquatic Sports', icon: '🏊', popular: true },
  { name: 'Athletics/Track & Field', category: 'Individual Sports', icon: '🏃', popular: true },
  { name: 'Gymnastics', category: 'Artistic Sports', icon: '🤸', popular: true },
  { name: 'Boxing', category: 'Combat Sports', icon: '🥊', popular: true },
  { name: 'Cycling', category: 'Individual Sports', icon: '🚴', popular: true },
  { name: 'Baseball', category: 'Team Sports', icon: '⚾', popular: true },

  // Major World Sports
  { name: 'Cricket', category: 'Team Sports', icon: '🏏', popular: true },
  { name: 'Rugby', category: 'Team Sports', icon: '🏉', popular: true },
  { name: 'Hockey (Ice)', category: 'Team Sports', icon: '🏒', popular: true },
  { name: 'Hockey (Field)', category: 'Team Sports', icon: '🏑', popular: true },
  { name: 'Golf', category: 'Individual Sports', icon: '⛳', popular: true },

  // Combat Sports
  { name: 'Wrestling', category: 'Combat Sports', icon: '🤼', popular: false },
  { name: 'Judo', category: 'Combat Sports', icon: '🥋', popular: false },
  { name: 'Karate', category: 'Combat Sports', icon: '🥋', popular: false },
  { name: 'Taekwondo', category: 'Combat Sports', icon: '🥋', popular: false },
  { name: 'Kung Fu', category: 'Combat Sports', icon: '🥋', popular: false },
  { name: 'Mixed Martial Arts (MMA)', category: 'Combat Sports', icon: '🥊', popular: true },
  { name: 'Kickboxing', category: 'Combat Sports', icon: '🥊', popular: false },
  { name: 'Muay Thai', category: 'Combat Sports', icon: '🥊', popular: false },
  { name: 'Fencing', category: 'Combat Sports', icon: '🤺', popular: false },

  // Racquet Sports
  { name: 'Badminton', category: 'Racquet Sports', icon: '🏸', popular: true },
  { name: 'Table Tennis/Ping Pong', category: 'Racquet Sports', icon: '🏓', popular: true },
  { name: 'Squash', category: 'Racquet Sports', icon: '🎾', popular: false },
  { name: 'Racquetball', category: 'Racquet Sports', icon: '🎾', popular: false },
  { name: 'Pickleball', category: 'Racquet Sports', icon: '🏸', popular: false },

  // Aquatic Sports
  { name: 'Diving', category: 'Aquatic Sports', icon: '🤿', popular: false },
  { name: 'Water Polo', category: 'Aquatic Sports', icon: '🤽', popular: false },
  { name: 'Synchronized Swimming', category: 'Aquatic Sports', icon: '🏊', popular: false },
  { name: 'Surfing', category: 'Aquatic Sports', icon: '🏄', popular: true },
  { name: 'Rowing', category: 'Aquatic Sports', icon: '🚣', popular: false },
  { name: 'Canoeing/Kayaking', category: 'Aquatic Sports', icon: '🛶', popular: false },
  { name: 'Sailing', category: 'Aquatic Sports', icon: '⛵', popular: false },

  // Winter Sports
  { name: 'Skiing (Alpine)', category: 'Winter Sports', icon: '⛷️', popular: true },
  { name: 'Skiing (Cross-Country)', category: 'Winter Sports', icon: '⛷️', popular: false },
  { name: 'Snowboarding', category: 'Winter Sports', icon: '🏂', popular: true },
  { name: 'Ice Skating', category: 'Winter Sports', icon: '⛸️', popular: true },
  { name: 'Figure Skating', category: 'Winter Sports', icon: '⛸️', popular: false },
  { name: 'Speed Skating', category: 'Winter Sports', icon: '⛸️', popular: false },
  { name: 'Curling', category: 'Winter Sports', icon: '🥌', popular: false },
  { name: 'Bobsled', category: 'Winter Sports', icon: '🛷', popular: false },
  { name: 'Luge', category: 'Winter Sports', icon: '🛷', popular: false },

  // Fitness & Wellness
  { name: 'Yoga', category: 'Fitness & Wellness', icon: '🧘', popular: true },
  { name: 'Pilates', category: 'Fitness & Wellness', icon: '🧘', popular: false },
  { name: 'CrossFit', category: 'Fitness & Wellness', icon: '💪', popular: true },
  { name: 'Aerobics', category: 'Fitness & Wellness', icon: '💃', popular: false },
  { name: 'Zumba', category: 'Fitness & Wellness', icon: '💃', popular: false },
  { name: 'Bodybuilding', category: 'Fitness & Wellness', icon: '💪', popular: false },
  { name: 'Powerlifting', category: 'Fitness & Wellness', icon: '🏋️', popular: false },
  { name: 'Weightlifting', category: 'Fitness & Wellness', icon: '🏋️', popular: false },

  // Extreme Sports
  { name: 'Skateboarding', category: 'Extreme Sports', icon: '🛹', popular: true },
  { name: 'BMX', category: 'Extreme Sports', icon: '🚴', popular: false },
  { name: 'Rock Climbing', category: 'Extreme Sports', icon: '🧗', popular: true },
  { name: 'Parkour', category: 'Extreme Sports', icon: '🤸', popular: false },
  { name: 'Bungee Jumping', category: 'Extreme Sports', icon: '🪂', popular: false },
  { name: 'Skydiving', category: 'Extreme Sports', icon: '🪂', popular: false },
  { name: 'Paragliding', category: 'Extreme Sports', icon: '🪂', popular: false },

  // Target Sports
  { name: 'Archery', category: 'Target Sports', icon: '🏹', popular: false },
  { name: 'Shooting', category: 'Target Sports', icon: '🎯', popular: false },
  { name: 'Darts', category: 'Target Sports', icon: '🎯', popular: false },

  ];

      {/* Support section removed per request */}
type Event = {
  _id: string;
  startDate: string;
  time?: string;
  location?: any;
  capacity?: any;
  sport?: string;
  organizer?: {
    _id: string;
    username: string;
    avatar?: string;
  };
  participants?: any[];
  maxParticipants?: number;
};

export default function Dashboard({ token, onNavigate, onViewProfile }: any) {
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [eventsFilter, setEventsFilter] = useState<'all' | 'free' | 'paid'>(() => {
    const saved = localStorage.getItem('auralink-dashboard-events-filter');
    return (saved as any) || 'all';
  });
  const [showEvents, setShowEvents] = useState<boolean>(() => {
    const saved = localStorage.getItem('auralink-dashboard-show-events');
    return saved ? JSON.parse(saved) : false;
  });
  // (Removed duplicate GlobalSearch render) Only render inside the main layout below.
  const [loading, setLoading] = useState(true);
  // Notifications removed from Dashboard
  // Persist dashboard UI state
  useEffect(() => {
    localStorage.setItem('auralink-dashboard-events-filter', eventsFilter);
  }, [eventsFilter]);
  useEffect(() => {
    localStorage.setItem('auralink-dashboard-show-events', JSON.stringify(showEvents));
  }, [showEvents]);
  const [createEventModalOpen, setCreateEventModalOpen] = useState(false);
  const [selectedSport, setSelectedSport] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'dashboard' | 'allEvents'>(
    'dashboard',
  );
  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | undefined>(undefined);
  const [participantsModalEvent, setParticipantsModalEvent] = useState<any | null>(null);
  const [createServiceModalOpen, setCreateServiceModalOpen] = useState(false);
  const [createProductModalOpen, setCreateProductModalOpen] = useState(false);

  useEffect(() => {
    if (token) {
      loadDashboardData();
      try {
        const payload = JSON.parse(atob(token.split('.')[1])) || {};
        setCurrentUserId(payload.id || payload._id);
      } catch {
        // If token parsing fails, try localStorage fallback
        try {
          const raw = localStorage.getItem('user');
          if (raw) {
            const u = JSON.parse(raw);
            setCurrentUserId(u?._id || u?.id);
          } else {
            setCurrentUserId(undefined);
          }
        } catch {
          setCurrentUserId(undefined);
        }
      }
    } else {
      // No token: still try to infer user from localStorage
      try {
        const raw = localStorage.getItem('user');
        if (raw) {
          const u = JSON.parse(raw);
          setCurrentUserId(u?._id || u?.id);
        } else {
          setCurrentUserId(undefined);
        }
      } catch {
        setCurrentUserId(undefined);
      }
    }
  }, [token]);

  async function loadDashboardData() {
    try {
      setLoading(true);
      // Load upcoming events (next 30 days)
      // Focus Dashboard on events the user has joined
      const eventsRes = await axios.get(`${API}/api/events/my/joined?limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const events = (eventsRes.data.events || []).filter((event: Event) => {
        const eventDate = new Date(event.startDate);
        const now = new Date();
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(now.getDate() + 30);
        return eventDate >= now && eventDate <= thirtyDaysFromNow;
      });

      setUpcomingEvents(events);

      // Notifications removed from Dashboard
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  }
  const stats = {
    upcomingEvents: upcomingEvents.length,
  };

  // Open Event Details (populated) by id
  const openEventDetails = async (eventId: string) => {
    try {
      const resp = await axios.get(`${API}/api/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedEvent(resp.data);
    } catch (e) {
      // Fallback to locally loaded list if available
      const fallback = upcomingEvents.find((e: any) => e._id === eventId) || null;
      setSelectedEvent(fallback);
    }
  };

  const openParticipantsModal = async (eventObj: any) => {
    try {
      const resp = await axios.get(`${API}/api/events/${eventObj._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setParticipantsModalEvent(resp.data);
    } catch (e) {
      setParticipantsModalEvent(eventObj);
    }
    setSelectedEvent(null);
  };

  // Basic join handler: route to Discover for full join/payment flow
  const handleJoinFromDashboard = (eventId: string) => {
    if (onNavigate) onNavigate('discover');
  };

  // Message organizer fallback: create conversation then navigate home
  const handleMessageOrganizer = async (organizerId: string) => {
    if (!token) return;
    try {
      const res = await axios.post(
        `${API}/api/conversations`,
        { partnerId: organizerId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      localStorage.setItem('auralink-active-conversation', JSON.stringify(res.data));
      localStorage.setItem('auralink-in-dm', 'true');
      window.location.href = '/';
    } catch (err) {
      console.error('Failed to start conversation:', err);
    }
  };

  // View Mode Routing (booking-related routes removed)

  if (viewMode === 'allEvents') {
    return (
      <>
        <AllEvents
          token={token}
          onBack={() => setViewMode('dashboard')}
          onNavigate={onNavigate}
          onViewEvent={(id: string) => openEventDetails(id)}
        />
        <EventDetailModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onJoin={handleJoinFromDashboard}
          onMessage={handleMessageOrganizer}
          onViewParticipants={openParticipantsModal}
          currentUserId={currentUserId}
        />
        {participantsModalEvent && (
          <EventParticipantsModal
            event={participantsModalEvent}
            onClose={() => setParticipantsModalEvent(null)}
            onMessage={handleMessageOrganizer}
            onApproveRequest={async (eventId: string, requestId: string) => {
              try {
                await axios.post(
                  `${API}/api/events/${eventId}/approve-request/${requestId}`,
                  {},
                  { headers: { Authorization: `Bearer ${token}` } },
                );
                const refreshed = await axios.get(`${API}/api/events/${eventId}`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                setParticipantsModalEvent(refreshed.data);
              } catch (err) {
                console.error('Approve error:', err);
              }
            }}
            onRejectRequest={async (eventId: string, requestId: string) => {
              try {
                await axios.post(
                  `${API}/api/events/${eventId}/reject-request/${requestId}`,
                  {},
                  { headers: { Authorization: `Bearer ${token}` } },
                );
                const refreshed = await axios.get(`${API}/api/events/${eventId}`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                setParticipantsModalEvent(refreshed.data);
              } catch (err) {
                console.error('Reject error:', err);
              }
            }}
            currentUserId={currentUserId}
            isOrganizer={participantsModalEvent?.organizer?._id === currentUserId}
          />
        )}
      </>
    );
  }

  // Notifications page route removed from Dashboard

  // If viewing a specific sport's events, show that component
  if (selectedSport) {
    return (
      <SportEvents sport={selectedSport} token={token} onBack={() => setSelectedSport(null)} />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen themed-page p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 rounded w-1/3" style={{ background: 'var(--muted)' }}></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-32 rounded-2xl themed-card"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen themed-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-heading mb-2">
              Welcome to TeaKonn 👋 <Sparkles className="inline w-6 h-6 text-accent align-text-bottom ml-1" />
            </h1>
            <p className="text-theme-secondary">
              Your home for events, socials and venues — plus a simple marketplace for services (coaching, training) and merchandise. All powered by real‑time chat.
            </p>
            <p className="text-theme-secondary text-xs mt-1">
              Quick guide: Discover events • Join or create • Chat in Messages • List services/merch • Update status
            </p>
          </div>
        </div>

        {/* Inline Dashboard search (single input, no overlay) */}
        <div className="rounded-2xl p-4 themed-card">
          <DashboardSearch token={token} onNavigate={onNavigate} onViewProfile={onViewProfile} />
        </div>

        {/* Group Chats moved to sidebar */}

        {/* Ready to Train Banner - moved to top */}
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-3xl p-8 md:p-12 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24"></div>

          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <Trophy className="w-8 h-8" />
              <h2 className="text-3xl font-bold">Ready to Train?</h2>
            </div>
            <p className="text-lg mb-6 text-white/90">
              Join events, connect with athletes, and take your fitness to the next level!
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => onNavigate && onNavigate('discover')}
                className="px-6 py-3 bg-white text-accent font-bold rounded-xl border border-accent/30 hover:border-accent/50 hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center gap-2"
              >
                <Sparkles className="w-5 h-5" />
                Discover Events
              </button>
              <button
                onClick={() => setCreateEventModalOpen(true)}
                className="px-6 py-3 bg-white/20 backdrop-blur-sm text-white font-bold rounded-xl hover:bg-white/30 transition-all duration-300 border-2 border-white/50 flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Create Event
              </button>
              <button
                onClick={() => setCreateServiceModalOpen(true)}
                className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white font-bold rounded-xl hover:from-purple-600 hover:to-pink-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Create Service
              </button>
              <button
                onClick={() => setCreateProductModalOpen(true)}
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Sell Product
              </button>
              <button
                onClick={() => {
                  onNavigate && onNavigate('past-events');
                }}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center gap-2"
              >
                <Calendar className="w-5 h-5" />
                Past Events
              </button>
              <button
                onClick={() => {
                  /* Past Events button removed per request */
                }}
                className="hidden"
              >
                {/* Removed */}
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards (bookings removed) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* All Events - Clickable */}
          <button
            onClick={() => setViewMode('allEvents')}
            className="rounded-2xl p-6 hover:shadow-xl hover:scale-105 transition-all duration-300 text-left group relative overflow-hidden"
            style={{
              background: 'var(--card)',
              border: '2px solid var(--border)',
              color: 'var(--text)',
            }}
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
            <div className="relative flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 dark:text-purple-400 mb-1 font-medium">
                  All Events
                </p>
                <p className="text-3xl font-bold text-heading">{stats.upcomingEvents}</p>
                <p className="text-xs text-theme-secondary mt-1">Upcoming events</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/30 group-hover:rotate-12 transition-transform">
                <Calendar className="w-6 h-6 text-white" />
              </div>
            </div>
          </button>

          {/* Notifications card removed */}

          {/* Get Verified */}
          <button
            onClick={() => onNavigate && onNavigate('verify-request')}
            className="rounded-2xl p-6 hover:shadow-xl hover:scale-105 transition-all duration-300 text-left group relative overflow-hidden"
            style={{
              background: 'var(--card)',
              border: '2px solid var(--border)',
              color: 'var(--text)',
            }}
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
            <div className="relative flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600 dark:text-emerald-400 mb-1 font-medium">
                  Get Verified
                </p>
                <p className="text-3xl font-bold text-heading">Pro / Venue</p>
                <p className="text-xs text-theme-secondary mt-1">Request a verification badge</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/30 group-hover:rotate-12 transition-transform">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
            </div>
          </button>
        </div>

        {/* Support TeaKonn section removed per request */}

        {/* Past Events link removed per request */}

        {/* Ready to Train Banner moved to top */}

        {/* Community Events section removed */}
        {/* End content container */}
      </div>
      {/* Create Event Modal */}
      <CreateEventModal
        isOpen={createEventModalOpen}
        onClose={() => setCreateEventModalOpen(false)}
        token={token}
        onSuccess={() => {
          loadDashboardData();
          setCreateEventModalOpen(false);
        }}
      />
      {/* Event Detail Modal (Dashboard view) */}
      <EventDetailModal
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        onJoin={handleJoinFromDashboard}
        onMessage={handleMessageOrganizer}
        onViewParticipants={openParticipantsModal}
        onLeave={async (eventId: string) => {
          try {
            await axios.post(`${API}/api/events/${eventId}/leave`, {}, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const refreshed = await axios.get(`${API}/api/events/${eventId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            setSelectedEvent(refreshed.data);
            loadDashboardData();
          } catch (err) {
            console.error('Leave event error:', err);
          }
        }}
        currentUserId={currentUserId}
      />
      {/* Create Service Modal */}
      <CreateServiceModal
        isOpen={createServiceModalOpen}
        onClose={() => setCreateServiceModalOpen(false)}
        token={token}
        onServiceCreated={() => {
          setCreateServiceModalOpen(false);
        }}
      />
      {/* Create Product Modal */}
      <CreateProductModal
        isOpen={createProductModalOpen}
        onClose={() => setCreateProductModalOpen(false)}
        token={token}
        onSaved={() => {
          setCreateProductModalOpen(false);
        }}
      />
      {participantsModalEvent && (
        <EventParticipantsModal
          event={participantsModalEvent}
          onClose={() => setParticipantsModalEvent(null)}
          onMessage={handleMessageOrganizer}
          onApproveRequest={async (eventId: string, requestId: string) => {
            try {
              await axios.post(
                `${API}/api/events/${eventId}/approve-request/${requestId}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } },
              );
              const refreshed = await axios.get(`${API}/api/events/${eventId}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              setParticipantsModalEvent(refreshed.data);
            } catch (err) {
              console.error('Approve error:', err);
            }
          }}
          onRejectRequest={async (eventId: string, requestId: string) => {
            try {
              await axios.post(
                `${API}/api/events/${eventId}/reject-request/${requestId}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } },
              );
              const refreshed = await axios.get(`${API}/api/events/${eventId}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              setParticipantsModalEvent(refreshed.data);
            } catch (err) {
              console.error('Reject error:', err);
            }
          }}
          currentUserId={currentUserId}
          isOrganizer={participantsModalEvent?.organizer?._id === currentUserId}
        />
      )}
    </div>
  );
}
